"use client";
import { Box, Flex, Heading, List, ListItem, Text } from "@chakra-ui/react";
import { SectionTemplate } from "components/individuals/blocks";
import { ImageFragment } from "lib/gcms";
import Image from "next/legacy/image";

export interface FeaturesListItemProps {
  title: string;
  description: string;
  /**
   * An array of list sub-items (i.e. `Wind Damage`) displayed under a list item (i.e. `Infrastructure Vulnerabilities`)
   */
  featuresListSubitem?: {
    title: string;
    description: string;
    featuresListSubitem?: {
      title: string;
      description: string;
    }[];
  }[];
  icon?: ImageFragment;
  alt?: string;
}
export interface FeaturesListSectionProps {
  title: string;
  subtitle: string;
  bg: string;
  color: string;
  titleColor: string;
  subtitleColor: string;
  featuresList: FeaturesListItemProps[];
  image: ImageFragment & { alt?: string };
}

export const FeaturesListSection = ({
  title,
  subtitle,
  bg,
  color,
  titleColor,
  subtitleColor,
  featuresList,
  image,
}: FeaturesListSectionProps) => {
  return (
    <SectionTemplate bg={bg} borderRadius={0}>
      <Box maxW="1215px">
        <Heading
          as={"h2"}
          color={titleColor}
          fontWeight={"bold"}
          fontSize={{
            base: 26,
            md: 34,
            lg: 38,
            xl: 45,
          }}
          lineHeight={1.2}
        >
          {title}
        </Heading>
        <Text
          color={subtitleColor}
          pt={3}
          fontWeight={"bold"}
          fontSize={{
            base: 14,
            md: 16,
            lg: 17,
            xl: 18,
          }}
        >
          {subtitle}
        </Text>
        <Flex
          flexDir={{
            base: "column",
            lg: "row",
          }}
          columnGap={20}
          align={"center"}
          pt={{
            base: 8,
            lg: 12,
          }}
        >
          <List
            w={{
              lg: "50%",
            }}
            spacing={6}
          >
            {featuresList.map((item, idx) => (
              <ListItem key={`features-list-item-${idx}`}>
                <Flex flexDir={"row"}>
                  {item.icon && (
                    <Box w={"10%"}>
                      <Image
                        {...item.icon}
                        src={item.icon.url}
                        placeholder={
                          typeof item?.icon?.blurDataURL === "string"
                            ? "blur"
                            : undefined
                        }
                        alt="Blue checkmark icon"
                        blurDataURL={
                          typeof item?.icon?.blurDataURL === "string"
                            ? item?.icon?.blurDataURL
                            : undefined
                        }
                      />
                    </Box>
                  )}
                  <Box color={color} w={"90%"}>
                    <Text
                      fontWeight={"bold"}
                      fontSize={{
                        base: 14,
                        md: 16,
                        lg: 17,
                        xl: 18,
                      }}
                    >
                      {item.title}
                    </Text>
                    <Text
                      fontSize={{
                        base: 13,
                        md: 15,
                        lg: 16,
                        xl: 17,
                      }}
                    >
                      {item.description}
                    </Text>
                  </Box>
                </Flex>
              </ListItem>
            ))}
          </List>

          <Box
            w={"50%"}
            display={{
              base: "none",
              lg: "block",
            }}
          >
            <Image
              {...image}
              src={image.url}
              placeholder={
                typeof image?.blurDataURL === "string" ? "blur" : undefined
              }
              blurDataURL={
                typeof image?.blurDataURL === "string"
                  ? image?.blurDataURL
                  : undefined
              }
            />
          </Box>
        </Flex>
      </Box>
    </SectionTemplate>
  );
};
